import { Flex } from '@/components/Grid';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/Input';
import { Text } from '@/components/Text';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  Score,
  ZxcvbnResult,
  zxcvbnAsync,
  zxcvbnOptions,
} from '@zxcvbn-ts/core';
import { useEffect, useState } from 'react';

const passwordStrengths = [
  '#c44b46',
  '#c44b46',
  '#e28e54',
  '#e5d055',
  '#788f76',
];

type Props = {
  onChange: (value: { password: string; ok: boolean; strength: Score }) => void;
  firstName: string;
  lastName: string;
  email: string;
  postalCode: string;
  disabled?: boolean;
  label?: string;
  id?: string;
};

export function PasswordField({
  firstName,
  lastName,
  email,
  postalCode,
  onChange,
  disabled,
  label = 'Wachtwoord',
  id = 'password',
}: Props) {
  const [password, setPassword] = useState('');
  const [fullyLoaded, setFullyLoaded] = useState(false);
  const [strength, setStrength] = useState<ZxcvbnResult | null>(null);
  const [visible, setVisible] = useState(false);

  // Lazyload large dictionaries
  useEffect(() => {
    Promise.all([
      import('@zxcvbn-ts/language-en'),
      import('@zxcvbn-ts/language-nl-be'),
      import('@zxcvbn-ts/language-common'),
    ]).then(([en, nl, common]) => {
      zxcvbnOptions.setOptions({
        translations: nl.translations,
        graphs: common.adjacencyGraphs,
        dictionary: {
          ...common.dictionary,
          ...nl.dictionary,
          ...en.dictionary,
        },
      });
    });
    setFullyLoaded(true);
  }, []);

  const deferredPassword = useDebouncedValue(password);
  const deferredFirstName = useDebouncedValue(firstName);
  const deferredLastName = useDebouncedValue(lastName);
  const deferredEmail = useDebouncedValue(email);
  const deferredPostalCode = useDebouncedValue(postalCode);

  useEffect(() => {
    if (!disabled && deferredPassword.length) {
      zxcvbnAsync(
        deferredPassword,
        [deferredFirstName, deferredLastName, deferredEmail, deferredPostalCode] // user input blacklist
      ).then((strength) => {
        setStrength(strength);
        onChange({
          ok: strength.score >= 3,
          password: deferredPassword,
          strength: strength.score,
        });
      });
    } else {
      setStrength(null);
    }
  }, [
    deferredPassword,
    deferredFirstName,
    deferredLastName,
    deferredEmail,
    deferredPostalCode,
    fullyLoaded,
    onChange,
    disabled,
  ]);

  return (
    <Flex dir="column">
      <Input
        autoComplete="new-password"
        label={label}
        type={visible ? 'text' : 'password'}
        id={id}
        value={password}
        required
        minLength={6}
        maxLength={128}
        disabled={disabled}
        onChange={(e) => setPassword(e.target.value)}
        append={
          <Icon
            onClick={() => setVisible((value) => !value)}
            icon={visible ? 'eye' : 'eye_closed'}
          />
        }
      />

      {strength != null && deferredPassword.length > 0 && (
        <>
          <div
            style={{
              height: '0.5rem',
              borderRadius: '1rem',
              backgroundColor: passwordStrengths[strength.score],
              width: `${(strength.score + 1) * 20}%`,
            }}
          />

          {strength.feedback.warning && (
            <Text tag="small" theme="error" role="alert">
              {strength.feedback.warning}
            </Text>
          )}

          {strength.feedback.suggestions.length > 0 && (
            <div role="alert">
              <h4>Tips</h4>
              {strength.feedback.suggestions.map((suggestion) => (
                <Text tag="small" key={suggestion}>
                  {suggestion}
                </Text>
              ))}
            </div>
          )}
        </>
      )}
    </Flex>
  );
}
